import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouteObject,
} from 'react-router-dom'
import { ErrorPage } from '../pages/errorPage.tsx'
import { Login } from '../features/auth/login.tsx'
import { Layout } from './layout.tsx'
import { isWebDomain, urls } from '../shared/urls.ts'
import { ChatPage } from '../pages/chatPage.tsx'
import { ProfilePage } from '../pages/profilePage.tsx'
import { OnboardingPage, OnboardingProvider } from '../pages/onboardingPage.tsx'
import { ProgramPage } from '../pages/programPage.tsx'
import { NotFoundPage } from '../pages/notFoundPage.tsx'
import { PaymentStatusPage } from '../pages/paymentStatusPage.tsx'
import { SubscriptionsPage } from '../pages/subscriptionsPage.tsx'
import { DemoPage } from '../pages/demoPage.tsx'
import { ContactsPage } from '../pages/contactsPage.tsx'
import { appVersion } from '../shared/lib/utils.ts'
import {
  AuthAccess,
  DemoAccess,
  LoginAccess,
  SubscriptionsAccess,
  RouteSaver,
} from './guards.tsx'
import { ProgramPage2 } from '../pages/programPage2.tsx'
import { ProgressPage } from '../pages/progressPage.tsx'
import { PracticePage } from '../pages/practicePage.tsx'
import { VocabPage } from '../pages/vocabularyPage.tsx'
import { WordsPage } from '../pages/wordsPage.tsx'
import { PasswordRecovery } from '../pages/passwordRecovery.tsx'
import { TrialInfoPage } from '../pages/trialInfoPage.tsx'
import { NativeLangPage } from '../pages/nativeLangPage.tsx'
import { GoogleAuthProvider } from '../features/auth/googleAuth.tsx'
import { MainPage } from '../pages/mainPage.tsx'
import { NotificationsPage } from '../pages/notificationsPage.tsx'

const chooseYourLevel = isWebDomain
  ? {
      path: urls.chooseyourlevel,
      element: <ProgramPage registration={true} />,
    }
  : {}

export const routeObjects: RouteObject[] = [
  {
    path: '/',
    element: (
      <OnboardingProvider>
        <AuthAccess>
          <Layout
            withMenu={false}
            withNavbar={true}
            mainContainer={true}
            scroll={true}
            customContainer={true}
            requireSubscription
          />
        </AuthAccess>
      </OnboardingProvider>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to={urls.main} />,
      },
      {
        path: urls.learningProfile('*'),
        element: <ProgramPage2 registration={false} />,
      },
    ],
  },

  {
    path: '/',
    element: (
      <AuthAccess>
        <Layout
          withMenu={false}
          withNavbar={true}
          mainContainer={true}
          white={true}
          requireSubscription
        />
      </AuthAccess>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: urls.main,
        element: <MainPage />,
      },
      {
        path: urls.notifications,
        element: <NotificationsPage />,
      },
      {
        path: urls.profile(),
        element: <ProfilePage />,
        children: [{ path: ':section', element: <ProfilePage /> }],
      },
      {
        path: urls.practice,
        element: <PracticePage />,
      },
      {
        path: urls.payment(':status', ':plan?'),
        element: <PaymentStatusPage />,
      },
      {
        path: urls.progress,
        element: <ProgressPage />,
      },
    ],
  },
  {
    path: '/',
    element: (
      <AuthAccess>
        <Layout
          customContainer={true}
          withMenu={false}
          withNavbar={false}
          white={true}
          requireSubscription
        />
      </AuthAccess>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: urls.lesson(':tag', '*'),
        element: <ChatPage />,
      },
      {
        path: urls.vocabLesson(':tag', '*'),
        element: <VocabPage />,
      },
      { path: urls.practiceWords(), element: <WordsPage /> },
      {
        path: urls.practiceWords('select', ':programTag'),
        element: <WordsPage />,
      },
    ],
  },
  {
    path: '/',
    element: (
      <RouteSaver>
        <Layout fullWidth={true} withMenu={false} withNavbar={false} />
      </RouteSaver>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: urls.onboarding(':step?'),
        element: <OnboardingPage />,
      },
    ],
  },
  {
    path: urls.login,
    element: (
      <RouteSaver>
        <LoginAccess>
          <GoogleAuthProvider>
            <Login registration={false} />
          </GoogleAuthProvider>
        </LoginAccess>
      </RouteSaver>
    ),
  },
  {
    path: urls.passwordRecovery,
    element: (
      <RouteSaver>
        <LoginAccess>
          <PasswordRecovery />
        </LoginAccess>
      </RouteSaver>
    ),
  },
  {
    path: urls.registration,
    element: (
      <RouteSaver>
        <LoginAccess>
          <Login registration={true} />
        </LoginAccess>
      </RouteSaver>
    ),
  },
  {
    path: urls.selectNativeLang,
    element: (
      <RouteSaver>
        <Layout fullWidth={true} withMenu={false} withNavbar={false} />
      </RouteSaver>
    ),
    children: [
      {
        index: true,
        element: <NativeLangPage />,
      },
    ],
  },
  {
    path: urls.contacts,
    element: <ContactsPage />,
  },
  {
    path: '/',
    element: (
      <RouteSaver>
        <LoginAccess>
          <Outlet />
        </LoginAccess>
      </RouteSaver>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: urls.subscriptions(),
        element: <SubscriptionsPage country="ru" />,
        children: [
          { path: ':section', element: <SubscriptionsPage country="ru" /> },
        ],
      },
      {
        path: urls.trialInfo,
        element: <TrialInfoPage />,
      },
    ],
  },
  {
    path: urls.subscriptions_kz,
    element: (
      <RouteSaver>
        <OnboardingProvider>
          <SubscriptionsAccess>
            <SubscriptionsPage country="kz" />
          </SubscriptionsAccess>
        </OnboardingProvider>
      </RouteSaver>
    ),
  },
  {
    element: <Layout white withMenu={false} withNavbar={false} />,
    path: '/',
    children: [
      {
        path: urls.demo(':infoBlock?'),
        element: (
          <OnboardingProvider>
            <DemoAccess>
              <DemoPage />
            </DemoAccess>
          </OnboardingProvider>
        ),
      },

      chooseYourLevel,
    ],
  },
  // Deprecated 10.04.2025
  //   {
  //     path: urls.confirm,
  //     element: (
  //       <RouteSaver>
  //         <ConfirmAccess>
  //           <ConfirmRegistration />
  //         </ConfirmAccess>
  //       </RouteSaver>
  //     ),
  //   },
  {
    path: '/version',
    element: appVersion,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]

export const router = createBrowserRouter(routeObjects)
